import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeSlash, CheckCircle, Warning, ArrowRight, SpinnerGap } from '@phosphor-icons/react';
import { authAPI } from '@/utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

/* PayGBot dark auth palette — mirrors landing + dashboard */
const AUTH_BG = '#0F1117';
const AUTH_PANEL = '#171A22';
const AUTH_CARD = '#1E222D';
const AUTH_BORDER = 'rgba(255,255,255,0.08)';

const INPUT_BASE =
  'w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 border transition-all ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/60';

const Logo = () => (
  <Link to="/" className="inline-flex items-center space-x-2" data-testid="reset-page-logo">
    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
      <span className="text-white font-bold text-xl">P</span>
    </div>
    <span className="text-2xl font-bold text-white tracking-tight">PayGBot</span>
  </Link>
);

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [emailHint, setEmailHint] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenError('No reset token provided. Please request a new password reset link.');
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await authAPI.validateResetToken(token);
      if (response.data.valid) {
        setTokenValid(true);
        setEmailHint(response.data.email_hint || '');
      } else {
        setTokenError(response.data.message || 'Invalid or expired reset token');
      }
    } catch (error) {
      setTokenError('Failed to validate reset token. Please request a new reset link.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword, confirmPassword);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const passwordMismatch = newPassword && confirmPassword && newPassword !== confirmPassword;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ backgroundColor: AUTH_BG }}
      data-testid="reset-page-shell"
    >
      {/* Ambient emerald glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #10b981, transparent)' }}
        />
        <div
          className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full opacity-10 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #34d399, transparent)' }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Header logo */}
        <div className="text-center mb-6">
          <Logo />
        </div>

        <div
          className="rounded-2xl border shadow-2xl p-8"
          style={{ backgroundColor: AUTH_CARD, borderColor: AUTH_BORDER }}
        >
          {/* Icon header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/25">
              <Lock size={32} className="text-white" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Reset Your Password</h1>
          </div>

          {/* Loading State */}
          {validating && (
            <div className="text-center py-8">
              <SpinnerGap size={40} className="text-emerald-400 animate-spin mx-auto mb-4" />
              <p className="text-slate-400">Validating reset link...</p>
            </div>
          )}

          {/* Token Invalid State */}
          {!validating && !tokenValid && (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 bg-red-500/15 border border-red-500/30">
                <Warning size={32} className="text-red-400" weight="fill" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Invalid Reset Link</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">{tokenError}</p>
              <Link
                to="/login"
                data-testid="back-to-login-link"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                Back to Login
                <ArrowRight size={18} />
              </Link>
            </div>
          )}

          {/* Success State */}
          {success && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-6"
            >
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 bg-emerald-500/15 border border-emerald-500/30">
                <CheckCircle size={32} className="text-emerald-400" weight="fill" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Password Reset Successfully!</h2>
              <p className="text-slate-400 mb-6">
                You can now log in with your new password.
              </p>
              <Link
                to="/login"
                data-testid="login-after-reset-link"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                Go to Login
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          )}

          {/* Reset Form */}
          {!validating && tokenValid && !success && (
            <>
              {emailHint && (
                <p className="text-center text-slate-400 mb-6">
                  Resetting password for{' '}
                  <span className="font-medium text-white">{emailHint}</span>
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-slate-200 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      data-testid="new-password-input"
                      className={`${INPUT_BASE} pr-12`}
                      style={{ backgroundColor: AUTH_PANEL, borderColor: AUTH_BORDER }}
                      placeholder="Enter new password (min 8 characters)"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="toggle-password-visibility"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 transition-colors focus:outline-none"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-200 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password-input"
                    aria-invalid={passwordMismatch ? 'true' : 'false'}
                    aria-describedby={passwordMismatch ? 'confirm-password-error' : undefined}
                    className={`${INPUT_BASE} ${
                      passwordMismatch ? 'border-red-500/60 focus:ring-red-500/30 focus:border-red-500' : ''
                    }`}
                    style={{ backgroundColor: AUTH_PANEL, borderColor: passwordMismatch ? undefined : AUTH_BORDER }}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                  {passwordMismatch && (
                    <p
                      id="confirm-password-error"
                      role="alert"
                      data-testid="password-mismatch-error"
                      className="text-red-400 text-sm mt-1.5 flex items-center gap-1.5"
                    >
                      <Warning size={14} weight="fill" />
                      Passwords do not match
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !newPassword || newPassword !== confirmPassword}
                  data-testid="reset-password-btn"
                  className="w-full py-3 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  {loading ? (
                    <>
                      <SpinnerGap size={20} className="animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* Back to login link */}
          {!success && (
            <p className="text-center text-slate-400 text-sm mt-6">
              Remember your password?{' '}
              <Link
                to="/login"
                className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors focus:outline-none focus:underline"
              >
                Back to Login
              </Link>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
